import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import FileUpload from "../../components/FileUpload";
import SpinnerLoader from "../../utils/SpinnerLoader";
import {
    cashIcon,
    claimedVolumeIcon,
    driverClaimPriceIcon,
    foodBeveragesIcon,
    maintenanceRepairIcon,
    miscellaneousIcon,
    sensorIcon,
    tollTaxesIcon,
    variableIcon,
    varianceIcon
} from "../../utils/svgs";
import FuelCard from "./FuelCard";

const FuelListDescription = () => {
    const [activeTab, setActiveTab] = useState('pettyCash');
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const { loading, getFuelLogDetailsData, getFuelLogDetailsLoading, fuelGeneralDetailsData } = useSelector((state) => state.fuel);

    const fuelTrendData = fuelGeneralDetailsData.map(item => ({
        x: new Date(item.refuel_at).getTime(),
        y: Number(item.claimed_fueled),
    }));

    useEffect(() => {
        if (getFuelLogDetailsData?.fuel_files?.length > 0) {
            setUploadedFiles(
                getFuelLogDetailsData?.fuel_files?.map((img) => ({
                    fileName: img.file_path,
                    originalName: img.file_path,
                    previewUrl: `${process.env.REACT_APP_API_FILES_URL}${img.file_path}`,
                    backend: true,
                    id: img.id
                }))
            );
        }

        return () => {
            setUploadedFiles([]);
        }
    }, [getFuelLogDetailsData])

    const fuelReconciliationItems = [
        {
            icon: driverClaimPriceIcon,
            label: "Driver Claim Price",
            unit: "PKR",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelClaimedPrice,
            valueColor: "text-primary text-decoration-underline"
        },
        {
            icon: claimedVolumeIcon,
            label: "Claimed Volume",
            unit: "ltrs",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelClaimedVolume
        },
        {
            icon: sensorIcon,
            label: "Sensor Data",
            unit: "ltrs",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelSensorData || "--",
            subValue: ""
        },
        {
            icon: variableIcon,
            label: "Remaining",
            unit: "PKR",
            value: getFuelLogDetailsData?.fuel_reconciliation?.variance,
            valueColor: "text-danger",
            isHighlight: true
        }
    ];

    const pettyCashItems = [
        {
            icon: cashIcon,
            label: "Expense Claim",
            unit: "PKR",
            value: getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0 // default to 0 if undefined
        },
        {
            icon: cashIcon,
            label: "Allocated Petty Cash",
            unit: "PKR",
            value: getFuelLogDetailsData?.allocated_petty_cash || 0 // default to 0 if undefined
        },
        {
            icon: varianceIcon,
            label: "Remaining",
            unit: "PKR",
            value: (getFuelLogDetailsData?.allocated_petty_cash || 0) - (getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0),
            valueColor: (getFuelLogDetailsData?.allocated_petty_cash || 0) - (getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0) < 0
                ? "text-danger"
                : "text-success",
            isHighlight: true
        }
    ];

    const getExpenseIcon = (title) => {
        if (!title) return miscellaneousIcon;
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes("toll")) return tollTaxesIcon;
        if (lowerTitle.includes("maintenance")) return maintenanceRepairIcon;
        if (lowerTitle.includes("food")) return foodBeveragesIcon;
        return miscellaneousIcon;
    };

    const expensesItems = (getFuelLogDetailsData?.total_trip_driver_expenses?.expenses || []).map(expense => ({
        icon: getExpenseIcon(expense.title),
        label: expense.title,
        value: expense.total_amount
    }));

    const pettyCashTabs = [
        {
            label: "Petty Cash",
            active: activeTab === 'pettyCash',
            onClick: () => setActiveTab('pettyCash')
        },
        {
            label: "Expenses",
            active: activeTab === 'expenses',
            onClick: () => setActiveTab('expenses')
        }
    ];

    if (getFuelLogDetailsLoading || loading) {
        return <SpinnerLoader />
    }

    return (
        <div className="fuel_list_desc_main">
            <Row>
                <Col md={6}>
                    <FuelCard
                        title="Fuel Reconciliation"
                        subtitle="Fuel Information for this trip record."
                        items={fuelReconciliationItems}
                    />
                </Col>
                <Col md={6}>
                    <FuelCard
                        title="Petty Cash Reconciliation"
                        subtitle="Petty Cash Information for this trip record."
                        tabs={pettyCashTabs}
                        items={activeTab === 'pettyCash' ? pettyCashItems : expensesItems}
                    />
                </Col>
                <Col md={12}>
                    <div className="photo_document_section mb-3">
                        <h6>Attachments</h6>
                        <p> You can attach up to 5 files, max 1MB each.</p>
                        <FileUpload
                            destination="fuel-logs"
                            files={uploadedFiles}
                            setFiles={setUploadedFiles}
                            maxFileSize={10}
                            tripId={getFuelLogDetailsData?.id}
                        />
                    </div>
                </Col>
                <Col md={12}>
                    <div className="photo_document_section mb-3">
                        <h6>Trip Fuel Trend</h6>
                        <p>Fuel consumption and refill patterns across trip duration.</p>

                        <ReactApexChart
                            type="line"
                            height={320}
                            series={[
                                {
                                    name: "Fuel Filled",
                                    data: fuelTrendData,
                                },
                            ]}
                            options={{
                                chart: {
                                    type: "line",
                                    zoom: { enabled: false },
                                    toolbar: { show: false },
                                },
                                stroke: { curve: "smooth", width: 2.8 },
                                markers: {
                                    size: 6,
                                    strokeWidth: 2,
                                    hover: { size: 8 },
                                },
                                colors: ["#F59E0B"],
                                xaxis: {
                                    type: "datetime",
                                    title: {
                                        text: "Refuel Date",
                                    },
                                    labels: {
                                        format: "dd MMM HH:mm",
                                    },
                                },
                                yaxis: {
                                    title: {
                                        text: "Fuel Filled (Liters)",
                                    },
                                    labels: {
                                        formatter: (val) => `${val} L`,
                                    },
                                },
                                tooltip: {
                                    shared: false,
                                    intersect: true,
                                    x: {
                                        format: "dd MMM yyyy HH:mm",
                                    },
                                    y: {
                                        formatter: (val) => `${val} Liters`,
                                    },
                                },
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default FuelListDescription;